// src/components/AboutLJDAgent.js
import React, { useEffect, useState } from "react";
import { marked } from "marked";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Link,
} from "@mui/material";
import { getVersion } from "../api";

/** Version (from the git tag) with a quiet link to the changelog. */
function VersionFooter() {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    getVersion().then((v) => alive && setInfo(v)).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!info?.version) return null;
  return (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ mr: "auto", pl: 1 }}>
        Version {info.version}
        {info.changelog && (
          <>
            {" · "}
            <Link component="button" variant="caption" onClick={() => setOpen(true)} sx={{ verticalAlign: "baseline" }}>
              What&apos;s new
            </Link>
          </>
        )}
      </Typography>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth aria-labelledby="changelog-title">
        <DialogTitle id="changelog-title">What&apos;s new</DialogTitle>
        <DialogContent
          dividers
          sx={{ "& h1": { display: "none" }, "& h2": { fontSize: "1rem", mt: 2, mb: 0.5 }, "& p, & li": { fontSize: "0.875rem" } }}
          dangerouslySetInnerHTML={{ __html: marked.parse(info.changelog) }}
        />
        <DialogActions>
          <Button onClick={() => setOpen(false)} size="small">Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function AboutLJDAgent({ open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="about-ljd-agent-title"
    >
      <DialogTitle id="about-ljd-agent-title">
        T4R Learning Journey Design Agent
      </DialogTitle>

      <DialogContent dividers>
        {/* 1) One-line description + project context */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" paragraph>
            The <strong>T4R Learning Journey Design Agent</strong> is a tool from{" "}
            <Link
              href="https://k8.design"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              <strong>K8 Institut für strategische Ästhetik</strong>
            </Link>{" "}
            for the{" "}
            <Link
              href="https://twin4resilience.eu"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              <strong>Twin4Resilience</strong>
            </Link>{" "}
            project, helping trainers designing learning journeys based on micro-learning units across the
            EDU, EID, GOV and TEC frameworks.
          </Typography>

          <Typography variant="body2" paragraph>
            This tool is designed for learning managers and facilitators, who are developing learning programs for LDTs in the public sector.
          </Typography>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* 2) Development & credits */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            <strong>Development & credits</strong>
          </Typography>

          <Typography variant="body2" paragraph>
            This tool has been developed at{" "}
            <Link
              href="https://k8.design"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              K8 Institut für strategische Ästhetik
            </Link>
            .
          </Typography>

          <Typography variant="body2" paragraph>
            Lead Developer & Agentic Workflow:{" "}
            <strong>
              <Link
                href="https://www.linkedin.com/in/chirag-bhuvaneshwara"
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                Chirag Bhuvaneshwara
              </Link>
            </strong>
            , (
            <Link href="mailto:chb@k8.design" underline="hover">
              chb@k8.design
            </Link>
            )
          </Typography>

          <Typography variant="body2" paragraph>
            Concept and Logic:{" "}
            <strong>
              Tobias Meyer, Julia Pierzina, Sónia Alves, Müge Gümüs, Henrik
              Elburn, Gregor Euskirchen and Soenke Zehle
            </strong>
          </Typography>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* 3) Agent capabilities & Tech Stack */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" paragraph>
            The <strong>K8-LJD Agent</strong>
          </Typography>

          <Typography variant="body2" component="ul" sx={{ pl: 3 }}>
            <li>
              supports <strong>human-in-the-loop curation</strong>: the agent
              suggests, but trainers decide, document, and refine;
            </li>
            <li>
              brings together the <strong>project context</strong> (goals,
              learner profiles, future roles, and existing MLUs);
            </li>
            <li>
              uses <strong>structured LLM prompts</strong> to combine these
              inputs into a draft teaching journey; and
            </li>
            <li>
              aims for an <strong>explainable process</strong> with large
              language models in the loop
            </li>
          </Typography>

          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            <strong>Tech stack & infrastructure</strong>
          </Typography>

          <Typography variant="body2" component="ul" sx={{ pl: 3 }}>
            <li>
              <strong>React</strong> for the frontend interface.
            </li>
            <li>
              <strong>FastAPI</strong> for orchestration and backend logic.
            </li>
            <li>
              <strong>Mistral</strong> LLM APIs (European provider). The
              models run on infrastructure in EU.
            </li>
            <li>
              <strong>OVHcloud</strong> hosts the backend within a European
              infrastructure stack.
            </li>
            <li>
              All other frameworks and libraries used in this prototype are{" "}
              <strong>open source</strong>.
            </li>
          </Typography>
        </Box>

        {/* 4) Small internal debugging tip */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Debugging tip (for internal testers): Press{" "}
            <strong>Alt + Shift + D</strong>:
            <br />
            First press → Debug panel appears at the top of the card.
            <br />
            Second press → Debug panel disappears again.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        {open && <VersionFooter />}
        <Button onClick={onClose} variant="contained" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
